'use client';

import useClickOutside from '@/hooks/useClickOutside';
import { useViewerLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { XIcon } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  MotionConfig,
  Transition,
  Variant,
} from 'motion/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

const MORPH_DURATION_MS = 320;
const TITLE_ANIM_MS = 280;
const MORPH_EASING = 'cubic-bezier(0.32, 0.72, 0.24, 1)';

type TriggerRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type MorphingDialogContextType = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  uniqueId: string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  triggerRect: TriggerRect | null;
  setTriggerRect: (rect: TriggerRect | null) => void;
  isContentPresent: boolean;
  setIsContentPresent: (v: boolean) => void;
  isMorphComplete: boolean;
  setIsMorphComplete: (v: boolean) => void;
  isDialogFrame: boolean;
  setIsDialogFrame: (v: boolean) => void;
};

const MorphingDialogContext =
  React.createContext<MorphingDialogContextType | null>(null);

function useMorphingDialog() {
  const context = useContext(MorphingDialogContext);
  if (!context) {
    throw new Error(
      'useMorphingDialog must be used within a MorphingDialogProvider'
    );
  }
  return context;
}

export type MorphingDialogProviderProps = {
  children: React.ReactNode;
  transition?: Transition;
};

function MorphingDialogProvider({
  children,
  transition,
}: MorphingDialogProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<TriggerRect | null>(null);
  const [isContentPresent, setIsContentPresent] = useState(false);
  const [isMorphComplete, setIsMorphComplete] = useState(false);
  const [isDialogFrame, setIsDialogFrame] = useState(false);
  const uniqueId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null!);

  const contextValue = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      uniqueId,
      triggerRef,
      triggerRect,
      setTriggerRect,
      isContentPresent,
      setIsContentPresent,
      isMorphComplete,
      setIsMorphComplete,
      isDialogFrame,
      setIsDialogFrame,
    }),
    [
      isOpen,
      uniqueId,
      triggerRect,
      isContentPresent,
      isMorphComplete,
      isDialogFrame,
    ]
  );

  return (
    <MorphingDialogContext.Provider value={contextValue}>
      <MotionConfig transition={transition}>{children}</MotionConfig>
    </MorphingDialogContext.Provider>
  );
}

export type MorphingDialogProps = {
  children: React.ReactNode;
  transition?: Transition;
};

function MorphingDialog({ children, transition }: MorphingDialogProps) {
  return (
    <MorphingDialogProvider>
      <MotionConfig transition={transition}>{children}</MotionConfig>
    </MorphingDialogProvider>
  );
}

export type MorphingDialogTriggerProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  onClick?: () => void;
};

function MorphingDialogTrigger({
  children,
  className,
  style,
  triggerRef,
  onClick,
}: MorphingDialogTriggerProps) {
  const {
    setIsOpen,
    isOpen,
    uniqueId,
    setTriggerRect,
    isContentPresent,
  } = useMorphingDialog();
  const { t } = useViewerLanguage();

  const openWithRect = useCallback(
    (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      setTriggerRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      setIsOpen(true);
    },
    [setIsOpen, setTriggerRect]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick && onClick();
      if (isOpen) {
        setIsOpen(false);
      } else {
        openWithRect(event.currentTarget);
      }
    },
    [isOpen, setIsOpen, onClick, openWithRect]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (isOpen) {
          setIsOpen(false);
        } else {
          openWithRect(event.currentTarget);
        }
      }
    },
    [isOpen, setIsOpen, openWithRect]
  );

  return (
    <button
      ref={triggerRef}
      type='button'
      className={cn('relative cursor-pointer', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        ...style,
        opacity: isContentPresent ? 0 : 1,
        visibility: isContentPresent ? 'hidden' : 'visible',
      }}
      aria-haspopup='dialog'
      aria-expanded={isOpen}
      aria-controls={`motion-ui-morphing-dialog-content-${uniqueId}`}
      aria-label={`${t('Open dialog')} ${uniqueId}`}
    >
      {children}
    </button>
  );
}

export type MorphingDialogContentProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function MorphingDialogContent({
  children,
  className,
  style,
}: MorphingDialogContentProps) {
  const {
    setIsOpen,
    isOpen,
    uniqueId,
    triggerRef,
    triggerRect,
    setIsContentPresent,
    setIsMorphComplete,
    setIsDialogFrame,
  } = useMorphingDialog();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentAnimRef = useRef<Animation | null>(null);
  const [firstFocusableElement, setFirstFocusableElement] =
    useState<HTMLElement | null>(null);
  const [lastFocusableElement, setLastFocusableElement] =
    useState<HTMLElement | null>(null);

  useEffect(() => {
    setIsContentPresent(true);
    return () => setIsContentPresent(false);
  }, [setIsContentPresent]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !triggerRect) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dx = triggerRect.left - rect.left;
    const dy = triggerRect.top - rect.top;
    const ds = triggerRect.width / rect.width;

    el.style.transformOrigin = 'top left';
    el.style.willChange = 'transform';

    if (currentAnimRef.current) {
      try {
        currentAnimRef.current.cancel();
      } catch {}
    }

    const anim = el.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(${ds})` },
        { transform: 'translate(0px, 0px) scale(1)' },
      ],
      {
        duration: MORPH_DURATION_MS,
        easing: MORPH_EASING,
        fill: 'both',
      }
    );
    currentAnimRef.current = anim;
    setIsDialogFrame(true);
    anim.onfinish = () => {
      setIsMorphComplete(true);
    };

    return () => {
      try {
        anim.cancel();
      } catch {}
    };
  }, [triggerRect, setIsMorphComplete, setIsDialogFrame]);

  useEffect(() => {
    if (isOpen) return;
    setIsMorphComplete(false);

    const timer = setTimeout(() => {
      const el = containerRef.current;
      if (!el || !triggerRect) return;

      if (currentAnimRef.current) {
        try {
          currentAnimRef.current.commitStyles();
        } catch {}
        try {
          currentAnimRef.current.cancel();
        } catch {}
      }

      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const dx = triggerRect.left - rect.left;
      const dy = triggerRect.top - rect.top;
      const ds = triggerRect.width / rect.width;

      setIsDialogFrame(false);

      const anim = el.animate(
        [
          { transform: 'translate(0px, 0px) scale(1)' },
          { transform: `translate(${dx}px, ${dy}px) scale(${ds})` },
        ],
        {
          duration: MORPH_DURATION_MS,
          easing: MORPH_EASING,
          fill: 'both',
        }
      );
      currentAnimRef.current = anim;
    }, TITLE_ANIM_MS);

    return () => clearTimeout(timer);
  }, [isOpen, triggerRect, setIsMorphComplete, setIsDialogFrame]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
      if (event.key === 'Tab') {
        if (!firstFocusableElement || !lastFocusableElement) return;

        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setIsOpen, firstFocusableElement, lastFocusableElement]);

  useEffect(() => {
    if (!isOpen) {
      triggerRef.current?.focus();
      return;
    }

    const scrollY = window.scrollY;
    const body = document.body;
    const previousStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    const focusableElements = containerRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements && focusableElements.length > 0) {
      setFirstFocusableElement(focusableElements[0] as HTMLElement);
      setLastFocusableElement(
        focusableElements[focusableElements.length - 1] as HTMLElement
      );
      (focusableElements[0] as HTMLElement).focus();
    }

    return () => {
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.left = previousStyles.left;
      body.style.right = previousStyles.right;
      body.style.width = previousStyles.width;
      body.style.overflow = previousStyles.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, triggerRef]);

  useClickOutside(containerRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  return (
    <div
      ref={containerRef}
      className={cn('overflow-hidden', className)}
      style={style}
      role='dialog'
      aria-modal='true'
      aria-labelledby={`motion-ui-morphing-dialog-title-${uniqueId}`}
      aria-describedby={`motion-ui-morphing-dialog-description-${uniqueId}`}
    >
      {children}
    </div>
  );
}

export type MorphingDialogContainerProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function MorphingDialogContainer({ children }: MorphingDialogContainerProps) {
  const { isOpen, uniqueId } = useMorphingDialog();
  const [mounted, setMounted] = useState(false);
  const [keepRendering, setKeepRendering] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setKeepRendering(true);
      return;
    }
    if (!keepRendering) return;
    const t = setTimeout(() => {
      setKeepRendering(false);
    }, TITLE_ANIM_MS + MORPH_DURATION_MS + 60);
    return () => clearTimeout(t);
  }, [isOpen, keepRendering]);

  if (!mounted) return null;

  const isRendering = isOpen || keepRendering;

  const stopEvents = {
    onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
    onTouchMove: (e: React.TouchEvent) => e.stopPropagation(),
    onTouchEnd: (e: React.TouchEvent) => e.stopPropagation(),
    onWheel: (e: React.WheelEvent) => e.stopPropagation(),
  };

  return createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key={`backdrop-${uniqueId}`}
            className='fixed inset-0 h-full w-full bg-black/40 backdrop-blur-xs dark:bg-black/60 z-40'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ duration: MORPH_DURATION_MS / 1000 }}
            {...stopEvents}
          />
        )}
      </AnimatePresence>
      {isRendering && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center'
          {...stopEvents}
        >
          {children}
        </div>
      )}
    </>,
    document.body
  );
}

export type MorphingDialogSectionProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function MorphingDialogSection({
  children,
  className,
  style,
}: MorphingDialogSectionProps) {
  const { isMorphComplete } = useMorphingDialog();

  return (
    <motion.div
      className={cn('overflow-hidden', className)}
      style={style}
      initial={{ height: 0 }}
      animate={{ height: isMorphComplete ? 'auto' : 0 }}
      transition={{ duration: 0.28, ease: [0.32, 0.72, 0.24, 1] }}
    >
      {children}
    </motion.div>
  );
}

export type MorphingDialogTitleProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function MorphingDialogTitle({
  children,
  className,
  style,
}: MorphingDialogTitleProps) {
  const { uniqueId, isMorphComplete } = useMorphingDialog();

  return (
    <motion.div
      className={className}
      style={style}
      id={`motion-ui-morphing-dialog-title-${uniqueId}`}
      initial={{ opacity: 0, y: -16 }}
      animate={
        isMorphComplete
          ? { opacity: 1, y: 0 }
          : { opacity: 0, y: -16 }
      }
      transition={{ duration: 0.22, ease: [0.32, 0.72, 0.24, 1], delay: 0 }}
    >
      {children}
    </motion.div>
  );
}

export type MorphingDialogSubtitleProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

function MorphingDialogSubtitle({
  children,
  className,
  style,
}: MorphingDialogSubtitleProps) {
  const { isDialogFrame } = useMorphingDialog();

  return (
    <motion.div
      className={className}
      style={style}
      animate={{
        paddingLeft: isDialogFrame ? 16 : 0,
        paddingRight: isDialogFrame ? 16 : 0,
      }}
      transition={{
        duration: MORPH_DURATION_MS / 1000,
        ease: [0.32, 0.72, 0.24, 1],
      }}
    >
      {children}
    </motion.div>
  );
}

export type MorphingDialogDescriptionProps = {
  children: React.ReactNode;
  className?: string;
  disableLayoutAnimation?: boolean;
  variants?: {
    initial: Variant;
    animate: Variant;
    exit: Variant;
  };
};

function MorphingDialogDescription({
  children,
  className,
  variants,
}: MorphingDialogDescriptionProps) {
  const { uniqueId } = useMorphingDialog();

  if (!variants) {
    return (
      <div className={className} id={`dialog-description-${uniqueId}`}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      variants={variants}
      className={className}
      initial='initial'
      animate='animate'
      exit='exit'
      id={`dialog-description-${uniqueId}`}
    >
      {children}
    </motion.div>
  );
}

export type MorphingDialogImageProps = {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

function MorphingDialogImage({
  src,
  alt,
  className,
  style,
  children,
}: MorphingDialogImageProps) {
  return (
    <div className='relative'>
      <img src={src} alt={alt} className={cn(className)} style={style} />
      {children}
    </div>
  );
}

export type MorphingDialogCloseProps = {
  children?: React.ReactNode;
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
  variants?: {
    initial: Variant;
    animate: Variant;
    exit: Variant;
  };
};

function MorphingDialogClose({
  children,
  className,
  variants,
  ref,
}: MorphingDialogCloseProps) {
  const { setIsOpen, uniqueId } = useMorphingDialog();
  const { t } = useViewerLanguage();

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <motion.button
      ref={ref}
      onClick={handleClose}
      type='button'
      aria-label={t('Close dialog')}
      key={`dialog-close-${uniqueId}`}
      className={cn('absolute top-6 right-6', className)}
      initial='initial'
      animate='animate'
      exit='exit'
      variants={variants}
    >
      {children || <XIcon size={24} />}
    </motion.button>
  );
}

export {
  MorphingDialog,
  MorphingDialogClose,
  MorphingDialogContainer,
  MorphingDialogContent,
  MorphingDialogDescription,
  MorphingDialogImage,
  MorphingDialogSection,
  MorphingDialogSubtitle,
  MorphingDialogTitle,
  MorphingDialogTrigger,
};
